// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The 「模组」 section of a filing (component design §9.2, §12): one row per module slot,
 * above the parts — modules are scanned first.
 *
 * A row takes a module code (scanned into the shared box, or typed here), and says what
 * came of it: the module's archive number, expandable to its contents (read only); or why it
 * cannot go in; or, for a module with no archive yet, 「现场建档」, which files it in a
 * nested dialog and puts its SN here when done (§9.3).
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ApiError } from "@/api/client";
import { api } from "@/api/endpoints";
import type { ArchiveItem, ModuleType } from "@/api/types";
import { moduleTypeLabel } from "@/components/BomModulesSummary";
import { ChevronIcon } from "@/components/icons";
import type { FitProblem } from "@/scanner/moduleCodes";
import { scanEnterHandlers } from "@/scanner/scanEnter";
import type { ModuleSlot, ModuleSlotState, ModuleSlotsState } from "@/scanner/useModuleSlots";

export function ModuleSlotsPanel({
  slots,
  modules,
  types,
  onFileHere,
}: {
  slots: ModuleSlot[];
  modules: ModuleSlotsState;
  types: ModuleType[];
  /** Opens the nested module filing for this slot; absent where nesting is not offered. */
  onFileHere?: (slot: ModuleSlot) => void;
}) {
  const { t, i18n } = useTranslation();
  const english = i18n.language === "en-US";
  const label = (code: string) => moduleTypeLabel(code, types, english);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (code: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  if (slots.length === 0) return null;
  return (
    <section className="module-slots" aria-label={t("moduleFiling.slotsTitle")}>
      <h3 className="panel-subtitle">
        {t("moduleFiling.slotsTitle")}{" "}
        <span className="hint">
          {t("moduleFiling.slotsProgress", { done: modules.filledCount, total: slots.length })}
        </span>
      </h3>
      <table className="slots module-slot-table">
        <thead>
          <tr>
            <th>{t("bom.modules.module")}</th>
            <th>{t("archive.position")}</th>
            <th className="col-serial">{t("moduleFiling.code")}</th>
            <th>{t("moduleFiling.state")}</th>
          </tr>
        </thead>
        <tbody>
          {slots.map((slot) => {
            const state = modules.states[slot.positionCode] ?? { status: "empty" };
            const isNext = modules.nextSlot === slot.positionCode;
            const expanded = open.has(slot.positionCode) && state.status === "fitted";
            return (
              <ModuleSlotRows
                key={slot.positionCode}
                slot={slot}
                state={state}
                value={modules.values[slot.positionCode] ?? ""}
                isNext={isNext}
                expanded={expanded}
                typeLabel={label(slot.moduleType)}
                label={label}
                onToggle={() => toggle(slot.positionCode)}
                onChange={(raw) => modules.set(slot.positionCode, raw)}
                onFileHere={onFileHere ? () => onFileHere(slot) : undefined}
                t={t}
              />
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function ModuleSlotRows({
  slot,
  state,
  value,
  isNext,
  expanded,
  typeLabel,
  label,
  onToggle,
  onChange,
  onFileHere,
  t,
}: {
  slot: ModuleSlot;
  state: ModuleSlotState;
  value: string;
  isNext: boolean;
  expanded: boolean;
  typeLabel: string;
  label: (code: string) => string;
  onToggle: () => void;
  onChange: (raw: string) => void;
  onFileHere?: () => void;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  // Typed into a box of its own and looked up on Enter or on leaving it: a lookup per
  // keystroke would flash 「未建档」 at every prefix of a code being typed.
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? value;
  const commit = (raw: string) => {
    setDraft(null);
    if (raw !== value) onChange(raw);
  };
  const className =
    state.status === "fitted"
      ? "slot-filled"
      : state.status === "empty" || state.status === "checking"
        ? isNext
          ? "slot-next"
          : undefined
        : "slot-installed";
  return (
    <>
      <tr className={className}>
        <td>
          <span className="module-badge">{typeLabel}</span>
        </td>
        <td>
          {state.status === "fitted" ? (
            <button
              type="button"
              className={expanded ? "row-toggle row-toggle-open" : "row-toggle"}
              aria-expanded={expanded}
              aria-label={t(expanded ? "tree.collapse" : "tree.expand", {
                name: slot.positionName,
              })}
              onClick={onToggle}
            >
              <ChevronIcon size={14} />
            </button>
          ) : (
            <span className="row-toggle-spacer" />
          )}
          {slot.positionName}
        </td>
        <td>
          <input
            aria-label={slot.positionCode}
            data-module-slot={slot.positionCode}
            className="mono"
            value={shown}
            autoComplete="off"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={(event) => commit(event.target.value)}
            {...scanEnterHandlers<HTMLInputElement>((target) => commit(target.value), {
              burst: true,
            })}
          />
        </td>
        <td>
          <SlotState state={state} label={label} onFileHere={onFileHere} t={t} />
        </td>
      </tr>
      {expanded && state.status === "fitted" && (
        <tr className="module-contents-row">
          <td colSpan={4}>
            <ModuleContents archiveId={state.module.archive_id} />
          </td>
        </tr>
      )}
    </>
  );
}

function SlotState({
  state,
  label,
  onFileHere,
  t,
}: {
  state: ModuleSlotState;
  label: (code: string) => string;
  onFileHere?: () => void;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  switch (state.status) {
    case "empty":
      return <span className="muted">{t("moduleFiling.waiting")}</span>;
    case "checking":
      return <span className="muted">{t("moduleFiling.checking")}</span>;
    case "fitted":
      return (
        <span>
          <Link to={`/archives/${state.module.archive_id}`} target="_blank" rel="noreferrer">
            {state.module.archive_no}
          </Link>{" "}
          <span className="mono hint">{state.module.module_sn}</span>{" "}
          <span className="hint">v{state.module.current_version}</span>
        </span>
      );
    case "duplicate":
      return (
        <span className="hint hint-error">
          {t("moduleFiling.duplicate", { archiveNo: state.module.archive_no })}
        </span>
      );
    case "refused":
      return <span className="hint hint-error">{problemText(state.problem, label, t)}</span>;
    case "not_found":
      return (
        <span className="hint hint-error">
          {t("moduleFiling.notFound")}{" "}
          {onFileHere && (
            <button type="button" onClick={onFileHere}>
              {t("moduleFiling.fileHere")}
            </button>
          )}
        </span>
      );
    case "failed":
      return (
        <span className="hint hint-error">
          {state.error instanceof ApiError
            ? state.error.localizedMessage
            : t("moduleFiling.lookupFailed")}
        </span>
      );
  }
}

export function problemText(
  problem: FitProblem,
  label: (code: string) => string,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  switch (problem.reason) {
    case "not_active":
      return t("moduleFiling.problem.not_active");
    case "type_mismatch":
      return t("moduleFiling.problem.type_mismatch", {
        actual: label(problem.actual),
        expected: label(problem.expected),
      });
    case "product_type_mismatch":
      return t("moduleFiling.problem.product_type_mismatch");
    case "bom_mismatch":
      return t("moduleFiling.problem.bom_mismatch", { theirs: problem.theirs ?? "—" });
    case "installed":
      return t("moduleFiling.problem.installed", { where: problem.where });
    case "incomplete":
      return t("moduleFiling.problem.incomplete", { slots: problem.empty.join(", ") });
  }
}

/** A fitted module's contents, read only, its own modules nested under them. */
function ModuleContents({ archiveId }: { archiveId: string }) {
  const { t } = useTranslation();
  const detail = useQuery({
    queryKey: ["archive", archiveId, "expanded"],
    queryFn: () => api.archives.get(archiveId, true),
  });
  if (!detail.data) return <span className="muted">{t("common.loading")}</span>;
  return (
    <ul className="module-contents">
      <ItemList items={detail.data.items} />
    </ul>
  );
}

function ItemList({ items }: { items: ArchiveItem[] }) {
  const { t } = useTranslation();
  return (
    <>
      {items.map((item) => (
        <li key={item.position_id}>
          <span>{item.position_name}</span>{" "}
          {item.module ? (
            <span className="mono hint">
              {item.module.archive_no} · {item.module.module_sn} · v{item.module.version_no}
            </span>
          ) : item.serial_raw ? (
            <span className="mono">{item.serial_raw}</span>
          ) : (
            <span className="hint">{t("archive.counted", { n: item.quantity })}</span>
          )}
          {item.children && item.children.length > 0 && (
            <ul>
              <ItemList items={item.children} />
            </ul>
          )}
        </li>
      ))}
    </>
  );
}
